import React, { useState, useMemo, memo } from 'react';
import { Icons } from '../Icons';
import { displayDomain } from '../../utils';

const CORE_METRICS = ['LCP', 'INP', 'CLS'];
const OTHER_METRICS = ['FCP', 'TTFB', 'TBT'];

const SortIcon = ({ direction }) => {
  if (!direction) return <Icons.ChevronDown size={12} className="text-slate-600" />;
  if (direction === 'asc') return <Icons.ChevronUp size={12} className="text-cyan-400" />;
  return <Icons.ChevronDown size={12} className="text-cyan-400" />;
};

const METRIC_THRESHOLDS = {
  LCP: { good: 2500, poor: 4000, unit: 'ms', format: v => v != null && !isNaN(Number(v)) ? `${(Number(v)/1000).toFixed(2)}s` : '—' },
  INP: { good: 200, poor: 500, unit: 'ms', format: v => v != null && !isNaN(Number(v)) ? `${Math.round(Number(v))}ms` : '—' },
  CLS: { good: 0.1, poor: 0.25, unit: '', format: v => v != null && !isNaN(Number(v)) ? Number(v).toFixed(3) : '—' },
  FCP: { good: 1800, poor: 3000, unit: 'ms', format: v => v != null && !isNaN(Number(v)) ? `${(Number(v)/1000).toFixed(2)}s` : '—' },
  TTFB: { good: 800, poor: 1800, unit: 'ms', format: v => v != null && !isNaN(Number(v)) ? `${Math.round(Number(v))}ms` : '—' },
  TBT: { good: 200, poor: 600, unit: 'ms', format: v => v != null && !isNaN(Number(v)) ? `${Math.round(Number(v))}ms` : '—' },
  FID: { good: 100, poor: 300, unit: 'ms', format: v => v != null && !isNaN(Number(v)) ? `${Math.round(Number(v))}ms` : '—' }
};

const DataSourceBadge = ({ source }) => {
  if (source === 'pagespeed') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-amber-500/20 text-amber-400 ml-2" title="Syntetická data z PageSpeed Insights">
        <Icons.Zap size={10} />
        PSI
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-blue-500/20 text-blue-400 ml-2" title="Reálná uživatelská data z CrUX">
      <Icons.Users size={10} />
      CrUX
    </span>
  );
};

const getStatusColor = (metric, value) => {
  if (value == null) return 'text-slate-400';
  const threshold = METRIC_THRESHOLDS[metric];
  if (!threshold) return 'text-slate-400';
  if (value <= threshold.good) return 'text-green-400';
  if (value <= threshold.poor) return 'text-yellow-400';
  return 'text-red-400';
};

const getStatusBg = (metric, value) => {
  if (value == null) return 'bg-slate-500/20';
  const threshold = METRIC_THRESHOLDS[metric];
  if (!threshold) return 'bg-slate-500/20';
  if (value <= threshold.good) return 'bg-green-500/20';
  if (value <= threshold.poor) return 'bg-yellow-500/20';
  return 'bg-red-500/20';
};

const getMetricValue = (domainData, metric, formFactor = 'ALL') => {
  if (!domainData?.data) return null;
  
  const data = domainData.data.byFormFactor?.[formFactor];
  if (!data?.metrics?.[metric]) return null;
  
  // API returns 'value' for p75 values
  return data.metrics[metric].value ?? data.metrics[metric].p75;
};

const getInteractivityMetric = (domainData, formFactor = 'ALL') => {
  const dataSource = domainData?.data?.dataSource || domainData?.dataSource;
  const data = domainData?.data?.byFormFactor?.[formFactor];
  
  if (dataSource === 'pagespeed') {
    const tbtValue = data?.metrics?.TBT?.value ?? data?.metrics?.TBT?.p75;
    return { value: tbtValue, metric: 'TBT', label: 'TBT' };
  }
  
  const inpValue = data?.metrics?.INP?.value ?? data?.metrics?.INP?.p75;
  return { value: inpValue, metric: 'INP', label: 'INP' };
};

const getDataSource = (domainData) => {
  return domainData?.data?.dataSource || domainData?.dataSource || 'crux';
};

const findBestValue = (comparisonData, metric, formFactor) => {
  let best = Infinity;
  comparisonData.forEach(d => {
    const val = getMetricValue(d, metric, formFactor);
    if (val !== null && val < best) best = val;
  });
  return best === Infinity ? null : best;
};

export const ComparisonTable = memo(({ comparisonData, formFactor = 'ALL' }) => {
  const [coreSort, setCoreSort] = useState({ column: null, direction: null });
  const [otherSort, setOtherSort] = useState({ column: null, direction: null });

  if (!comparisonData || comparisonData.length === 0) return null;

  const successfulDomains = comparisonData.filter(d => d.success);
  const failedDomains = comparisonData.filter(d => !d.success);

  if (successfulDomains.length === 0) {
    return (
      <div className="glass-card p-6 text-center">
        <Icons.Warning size={48} className="text-yellow-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Žádná data k porovnání</h3>
        <p className="text-slate-400">Pro žádnou z vybraných domén nebyla nalezena CrUX data.</p>
      </div>
    );
  }

  const handleSort = (column, sortState, setSortState) => {
    if (sortState.column === column) {
      if (sortState.direction === 'asc') setSortState({ column, direction: 'desc' });
      else if (sortState.direction === 'desc') setSortState({ column: null, direction: null });
      else setSortState({ column, direction: 'asc' });
    } else {
      setSortState({ column, direction: 'asc' });
    }
  };

  const sortData = (data, sortState, metrics, isCore = false) => {
    if (!sortState.column || !sortState.direction) return data;
    
    return [...data].sort((a, b) => {
      let aVal, bVal;
      if (sortState.column === 'domain') {
        aVal = displayDomain(a.origin).toLowerCase();
        bVal = displayDomain(b.origin).toLowerCase();
        return sortState.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      
      if (isCore && sortState.column === 'INP') {
        const aInteractivity = getInteractivityMetric(a, formFactor);
        const bInteractivity = getInteractivityMetric(b, formFactor);
        aVal = aInteractivity.value ?? Infinity;
        bVal = bInteractivity.value ?? Infinity;
      } else {
        aVal = getMetricValue(a, sortState.column, formFactor) ?? Infinity;
        bVal = getMetricValue(b, sortState.column, formFactor) ?? Infinity;
      }
      
      if (sortState.direction === 'asc') return aVal - bVal;
      return bVal - aVal;
    });
  };

  const sortedCoreDomains = sortData(successfulDomains, coreSort, CORE_METRICS, true);
  const sortedOtherDomains = sortData(successfulDomains, otherSort, OTHER_METRICS, false);

  return (
    <div className="space-y-6">
      <div className="glass-card overflow-hidden">
        <div className="p-4 border-b border-slate-700/50">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Icons.Scale size={20} className="text-purple-400" />
            Porovnání Core Web Vitals
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-800/50">
                <th 
                  className="px-4 py-3 text-left text-sm font-medium text-slate-400 cursor-pointer hover:text-white transition-colors select-none"
                  onClick={() => handleSort('domain', coreSort, setCoreSort)}
                >
                  <span className="inline-flex items-center gap-1">
                    Doména
                    <SortIcon direction={coreSort.column === 'domain' ? coreSort.direction : null} />
                  </span>
                </th>
                {CORE_METRICS.map(metric => (
                  <th 
                    key={metric} 
                    className="px-4 py-3 text-center text-sm font-medium text-slate-400 cursor-pointer hover:text-white transition-colors select-none"
                    onClick={() => handleSort(metric, coreSort, setCoreSort)}
                  >
                    <span className="inline-flex items-center justify-center gap-1">
                      {metric === 'INP' ? 'INP/TBT' : metric}
                      <SortIcon direction={coreSort.column === metric ? coreSort.direction : null} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedCoreDomains.map((domainData, idx) => {
                const dataSource = getDataSource(domainData);
                
                return (
                  <tr key={idx} className="border-t border-slate-700/30 hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <span className="text-white font-medium">{displayDomain(domainData.origin)}</span>
                        <DataSourceBadge source={dataSource} />
                      </div>
                    </td>
                    {CORE_METRICS.map(metric => {
                      if (metric === 'INP') {
                        const interactivity = getInteractivityMetric(domainData, formFactor);
                        const threshold = METRIC_THRESHOLDS[interactivity.metric];
                        const bestInp = findBestValue(successfulDomains, 'INP', formFactor);
                        const bestTbt = findBestValue(successfulDomains, 'TBT', formFactor);
                        const isBest = (interactivity.metric === 'INP' && interactivity.value === bestInp) ||
                                       (interactivity.metric === 'TBT' && interactivity.value === bestTbt);
                        
                        return (
                          <td key={metric} className="px-4 py-3 text-center">
                            {interactivity.value !== null && interactivity.value !== undefined ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${getStatusBg(interactivity.metric, interactivity.value)} ${getStatusColor(interactivity.metric, interactivity.value)}`}>
                                  {isBest && <Icons.Trophy size={14} className="text-yellow-400" />}
                                  {threshold?.format ? threshold.format(interactivity.value) : interactivity.value}
                                </span>
                                {interactivity.metric === 'TBT' && (
                                  <span className="text-xs text-slate-500">(TBT)</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-500">—</span>
                            )}
                          </td>
                        );
                      }
                      
                      const value = getMetricValue(domainData, metric, formFactor);
                      const bestValue = findBestValue(successfulDomains, metric, formFactor);
                      const isBest = value !== null && value === bestValue;
                      const threshold = METRIC_THRESHOLDS[metric];
                      
                      return (
                        <td key={metric} className="px-4 py-3 text-center">
                          {value !== null && value !== undefined ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${getStatusBg(metric, value)} ${getStatusColor(metric, value)}`}>
                              {isBest && <Icons.Trophy size={14} className="text-yellow-400" />}
                              {threshold?.format ? threshold.format(value) : value}
                            </span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="p-4 border-b border-slate-700/50">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Icons.Chart size={20} className="text-cyan-400" />
            Další metriky
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-800/50">
                <th 
                  className="px-4 py-3 text-left text-sm font-medium text-slate-400 cursor-pointer hover:text-white transition-colors select-none"
                  onClick={() => handleSort('domain', otherSort, setOtherSort)}
                >
                  <span className="inline-flex items-center gap-1">
                    Doména
                    <SortIcon direction={otherSort.column === 'domain' ? otherSort.direction : null} />
                  </span>
                </th>
                {OTHER_METRICS.map(metric => (
                  <th 
                    key={metric} 
                    className="px-4 py-3 text-center text-sm font-medium text-slate-400 cursor-pointer hover:text-white transition-colors select-none"
                    onClick={() => handleSort(metric, otherSort, setOtherSort)}
                  >
                    <span className="inline-flex items-center justify-center gap-1">
                      {metric}
                      <SortIcon direction={otherSort.column === metric ? otherSort.direction : null} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedOtherDomains.map((domainData, idx) => {
                const dataSource = getDataSource(domainData);
                
                return (
                  <tr key={idx} className="border-t border-slate-700/30 hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <span className="text-white font-medium">{displayDomain(domainData.origin)}</span>
                        <DataSourceBadge source={dataSource} />
                      </div>
                    </td>
                    {OTHER_METRICS.map(metric => {
                      const value = getMetricValue(domainData, metric, formFactor);
                      const bestValue = findBestValue(sortedOtherDomains, metric, formFactor);
                      const isBest = value !== null && value === bestValue;
                      const threshold = METRIC_THRESHOLDS[metric];
                      
                      return (
                        <td key={metric} className="px-4 py-3 text-center">
                          {value !== null && value !== undefined ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${getStatusBg(metric, value)} ${getStatusColor(metric, value)}`}>
                              {isBest && <Icons.Trophy size={14} className="text-yellow-400" />}
                              {threshold?.format ? threshold.format(value) : value}
                            </span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {failedDomains.length > 0 && (
        <div className="glass-card p-4 border-yellow-500/30">
          <div className="flex items-start gap-3">
            <Icons.Warning size={20} className="text-yellow-400 mt-0.5" />
            <div>
              <p className="text-yellow-400 font-medium mb-1">Některé domény nemají dostupná data:</p>
              <div className="flex flex-wrap gap-2">
                {failedDomains.map((d, idx) => (
                  <span key={idx} className="text-sm text-slate-400 bg-slate-800 px-2 py-1 rounded">
                    {displayDomain(d.origin)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="glass-card p-4 bg-slate-800/30">
        <div className="flex items-center gap-4 text-sm text-slate-400">
          <span className="font-medium">Legenda:</span>
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-blue-500/20 text-blue-400">
              <Icons.Users size={10} />
              CrUX
            </span>
            <span>= Reálná uživatelská data</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-amber-500/20 text-amber-400">
              <Icons.Zap size={10} />
              PSI
            </span>
            <span>= Syntetická data z PageSpeed</span>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ComparisonTable;
